import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  MenuItem,
  TextField
} from '@mui/material';
import { OptionType } from "../../types/helpers/types";

type MultiSelectAutocompleteProps = {
  options: OptionType[];
  selectedValues: { label: string, value: string | number }[];
  onChange: (newValues: { label: string, value: string | number }[] | null) => void;
  loading: boolean,
  textoVazio: string,
  textoLoading: string,
  textLabel: string,
  setOpen: Function,
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AutocompleteWithApi: React.FC<MultiSelectAutocompleteProps> = ({
  selectedValues,
  options,
  onChange,
  loading,
  textoVazio,
  textoLoading,
  textLabel,
  setOpen,
}) => {
  const allOptions = [
    { label: 'Selecionar todos', value: 'select_all', isSelectAll: true },
    ...options.sort((a, b) => {
      const aSelected = selectedValues.some((ele) => ele.value === a.value);
      const bSelected = selectedValues.some((ele) => ele.value === b.value);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.label.localeCompare(b.label);
    }),
  ];

  const truncate = (text: string) => text?.length > 15 ? `${text?.slice(0, 15)}...` : text;

  return (
    <Autocomplete
      fullWidth
      loadingText={textoLoading}
      noOptionsText={textoVazio}
      multiple
      disableCloseOnSelect
      disableClearable
      onOpen={() => setOpen(true)}
      loading={loading}
      options={allOptions.filter((element, index, array) => {
        const firstIndex = array.findIndex(item => item.value === element.value);
        return index === firstIndex;
      }) ?? []}
      value={selectedValues as OptionType[]}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={(event, newValue, reason, details) => {
        if (!details?.option) return;
        const { label, value, isSelectAll } = details.option;
        if (isSelectAll) {
          const allSelected =
            options.length > 0 &&
            options.every((opt) => selectedValues.some((ele) => ele.value === opt.value));

          const updatedList = allSelected ? [] : options;
          onChange(updatedList);
          return;
        }

        const alreadySelected = selectedValues.some((ele) => ele.value === value);
        const updatedList = alreadySelected
          ? selectedValues.filter((v) => v.value !== value)
          : [...selectedValues, { label: label, value: value }];

        onChange(updatedList);
      }}
      renderOption={(props, option, { selected }) => {
        if ((option as OptionType).isSelectAll) {
          const allSelected =
            options.length > 0 &&
            options.every((opt) => selectedValues.some((ele) => ele.value === opt.value));

          return (
            <MenuItem {...props} key="select_all">
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={allSelected}
              />
              <b>{loading ? textoLoading : allSelected ? 'Desmarcar todos' : 'Selecionar todos'}</b>
            </MenuItem>
          );
        }

        return (
          <MenuItem {...props} key={option.value}>
            <Checkbox
              disabled={loading}
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
              style={{ marginRight: 8 }}
            />
            <span>{option.label}</span>
          </MenuItem>
        );
      }}
      renderTags={(value, getTagProps) => {
        const displayedTags = value.slice(0, 2);
        return (
          <>
            {displayedTags.filter((element, index, array) => {
              const firstIndex = array.findIndex(item => item.value === element.value);
              return index === firstIndex;
            }).map((option, index) => {
              const tagProps = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={option.value}
                  label={truncate(option.label)}
                  onDelete={tagProps.onDelete}
                />
              );
            })}
            {value.length > 2 && (
              <Chip
                label={`+${value.length - 2}`}
                sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}
              />
            )}
          </>
        );
      }}
      renderInput={(params) => (
        <Box sx={{ position: 'relative', width: '100%' }}>
          <TextField {...params} label={textLabel} />
          {selectedValues.length > 0 && (
            <ClearAllIcon
              onClick={() => onChange(null)}
              sx={{
                position: 'absolute',
                right: 40,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'gray',
                cursor: 'pointer',
                '&:hover': { color: 'black' },
              }}
              titleAccess="Limpar seleção"
            />
          )}
        </Box>
      )}
    />
  );
};

export default AutocompleteWithApi;